import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Clock, 
  Calendar,
  Palette,
  Mail,
  Save,
  Loader2,
  Copy,
  ExternalLink,
  CreditCard,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle,
  AlertCircle,
  Link as LinkIcon,
  LayoutGrid,
  Users,
  Edit2,
  Info,
  HelpCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETTINGS_TAB_VALUES = new Set(['profile', 'tables', 'hours', 'booking', 'deposits', 'branding']);

function readInitialSettingsTab() {
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t && SETTINGS_TAB_VALUES.has(t)) return t;
  } catch (_) {
    /* ignore */
  }
  return 'profile';
}

const brandUrl = (file) => `${process.env.PUBLIC_URL || ''}/brand/${file}`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
};

/** Category chips from tables you already have + optional text for new names */
function TableCategoryPicker({ value, onChange, knownCategories, testIdPrefix = 'table' }) {
  const v = value ?? '';
  return (
    <div className="space-y-2">
      <Label>Category (optional)</Label>
      {knownCategories.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="Categories already in use"
        >
          {knownCategories.map((cat, idx) => (
            <Button
              key={cat}
              type="button"
              variant={v === cat ? 'default' : 'secondary'}
              size="sm"
              className={cn('h-9 shrink-0 rounded-full px-3', v === cat && 'ring-2 ring-primary/30')}
              onClick={() => onChange(cat)}
              data-testid={`${testIdPrefix}-category-chip-${idx}`}
            >
              {cat}
            </Button>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        <Label
          htmlFor={`${testIdPrefix}-category-new`}
          className="text-xs font-normal text-muted-foreground"
        >
          New category — type only if it is not in the list above
        </Label>
        <Input
          id={`${testIdPrefix}-category-new`}
          placeholder="e.g. Rooftop, Private room"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testIdPrefix}-table-category`}
        />
      </div>
    </div>
  );
}

const DEFAULT_RULE = {
  id: '',
  name: '',
  days: [],
  dates: [],  // Specific dates like Valentine's Day, etc.
  min_party_size: 2,
  deposit_type: 'fixed',
  amount: 20.0,
  currency: 'gbp',
  is_refundable: true,
  refund_hours_before: 24,
  enabled: true,
  priority: 0
};

export default function AdminSettings() {
  const { restaurant, updateRestaurant, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [gcalBulkBusy, setGcalBulkBusy] = useState(null);
  const [gcalRepairFrom, setGcalRepairFrom] = useState('');
  const [gcalRepairTo, setGcalRepairTo] = useState('');
  const [gcalRemoveOpen, setGcalRemoveOpen] = useState(false);
  const [gcalRemoveFrom, setGcalRemoveFrom] = useState('');
  const [gcalRemoveTo, setGcalRemoveTo] = useState('');
  const [gcalRemoveSource, setGcalRemoveSource] = useState('');
  const [gcalRemoveConfirm, setGcalRemoveConfirm] = useState('');
  const [gcalPurgeOpen, setGcalPurgeOpen] = useState(false);
  const [gcalPurgeFrom, setGcalPurgeFrom] = useState('');
  const [gcalPurgeTo, setGcalPurgeTo] = useState('');
  const [gcalPurgeConfirm, setGcalPurgeConfirm] = useState('');
  const [settingsTab, setSettingsTab] = useState(readInitialSettingsTab);

  // Tables state
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [newTable, setNewTable] = useState({ name: '', capacity: 2, category: '' });
  const [editingTable, setEditingTable] = useState(null);
  const [savingTable, setSavingTable] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loadingStaffUsers, setLoadingStaffUsers] = useState(false);
  const [savingStaffUser, setSavingStaffUser] = useState(false);
  const [newStaffUser, setNewStaffUser] = useState({
    name: '',
    pin: '',
    role: 'staff',
    permissions: ['dashboard', 'bookings', 'tablet'],
    is_active: true,
    phone_bookings_skip_deposit: false,
  });

  const canManageUsers = user?.role === 'admin' || (user?.permissions || []).includes('users');
  const permissionOptions = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'bookings', label: 'Bookings' },
    { key: 'tablet', label: 'Tablet View' },
    { key: 'settings', label: 'Settings' },
    { key: 'users', label: 'Manage Users' },
  ];

  const knownTableCategories = useMemo(() => {
    const s = new Set();
    for (const t of tables) {
      const c = typeof t.category === 'string' ? t.category.trim() : '';
      if (c) s.add(c);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [tables]);

  /** True when DB still has a Connect account id — covers failed /stripe/connect/status and “stuck” onboarding states */
  const stripeChargesLive = Boolean(stripeStatus?.charges_enabled);
  const stripeAccountPresent = Boolean(
    settings?.deposit_settings?.stripe_account_id ||
      stripeStatus?.account_id ||
      stripeStatus?.connected
  );
  const stripeOnboardingComplete = Boolean(
    stripeStatus?.onboarding_complete ?? settings?.deposit_settings?.stripe_onboarding_complete
  );

  useEffect(() => {
    fetchSettings();
    fetchTables();
  }, []);

  useEffect(() => {
    if (canManageUsers) {
      fetchStaffUsers();
    }
  }, [canManageUsers]);

  // Check for Stripe return
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Stripe account connected successfully!');
      checkStripeStatus();
    } else if (stripeParam === 'refresh') {
      toast.info('Please complete your Stripe onboarding');
    }
  }, [searchParams]);

  useEffect(() => {
    const g = searchParams.get('google_calendar');
    const err = searchParams.get('google_calendar_error');
    if (g === 'connected') {
      toast.success('Google Calendar connected.');
      fetchSettings();
      const u = new URL(window.location.href);
      u.searchParams.delete('google_calendar');
      u.searchParams.delete('google_calendar_error');
      window.history.replaceState({}, '', `${u.pathname}${u.search ? `?${u.searchParams.toString()}` : ''}`);
    }
    if (err) {
      toast.error(decodeURIComponent(err.replace(/\+/g, ' ')));
      const u = new URL(window.location.href);
      u.searchParams.delete('google_calendar');
      u.searchParams.delete('google_calendar_error');
      window.history.replaceState({}, '', `${u.pathname}${u.search ? `?${u.searchParams.toString()}` : ''}`);
    }
  }, [searchParams]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && SETTINGS_TAB_VALUES.has(t)) {
      setSettingsTab(t);
    }
  }, [searchParams]);

  const fetchTables = async () => {
    setLoadingTables(true);
    try {
      const response = await axios.get(`${API}/admin/tables`);
      setTables(response.data.tables || []);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    } finally {
      setLoadingTables(false);
    }
  };

  const createTable = async () => {
    if (!newTable.name || !newTable.capacity) {
      toast.error('Please enter table name and capacity');
      return;
    }
    setSavingTable(true);
    try {
      const response = await axios.post(`${API}/admin/tables`, {
        name: newTable.name.trim(),
        capacity: parseInt(newTable.capacity),
        category: newTable.category?.trim() || null
      });
      setTables([...tables, response.data.table]);
      setNewTable({ name: '', capacity: 2, category: '' });
      toast.success('Table created successfully');
    } catch (error) {
      toast.error('Failed to create table');
    } finally {
      setSavingTable(false);
    }
  };

  const fetchStaffUsers = async () => {
    setLoadingStaffUsers(true);
    try {
      const response = await axios.get(`${API}/admin/staff-users`);
      setStaffUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load staff users:', error);
    } finally {
      setLoadingStaffUsers(false);
    }
  };

  const createStaffUser = async () => {
    const pin = String(newStaffUser.pin || '').trim();
    if (!newStaffUser.name.trim()) {
      toast.error('Staff name is required');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    setSavingStaffUser(true);
    try {
      await axios.post(`${API}/admin/staff-users`, {
        name: newStaffUser.name.trim(),
        pin,
        role: newStaffUser.role,
        permissions: newStaffUser.role === 'admin' ? permissionOptions.map((p) => p.key) : newStaffUser.permissions,
        is_active: newStaffUser.is_active,
        phone_bookings_skip_deposit: newStaffUser.phone_bookings_skip_deposit,
      });
      setNewStaffUser({
        name: '',
        pin: '',
        role: 'staff',
        permissions: ['dashboard', 'bookings', 'tablet'],
        is_active: true,
        phone_bookings_skip_deposit: false,
      });
      await fetchStaffUsers();
      toast.success('PIN user added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setSavingStaffUser(false);
    }
  };

  const toggleStaffUserActive = async (staffUser) => {
    try {
      await axios.patch(`${API}/admin/staff-users/${staffUser.id}`, {
        is_active: !staffUser.is_active,
      });
      await fetchStaffUsers();
      toast.success(staffUser.is_active ? 'User deactivated' : 'User activated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const patchStaffPhoneBookingsDefault = async (staffUser, value) => {
    try {
      await axios.patch(`${API}/admin/staff-users/${staffUser.id}`, {
        phone_bookings_skip_deposit: value,
      });
      await fetchStaffUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const deleteStaffUser = async (staffUser) => {
    if (!window.confirm(`Delete ${staffUser.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/staff-users/${staffUser.id}`);
      await fetchStaffUsers();
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const updateTable = async (tableId) => {
    if (!editingTable) return;
    setSavingTable(true);
    try {
      const response = await axios.put(`${API}/admin/tables/${tableId}`, {
        name: String(editingTable.name).trim(),
        capacity: parseInt(editingTable.capacity),
        category: editingTable.category?.trim() || null,
        is_active: editingTable.is_active
      });
      setTables(tables.map(t => t.id === tableId ? response.data : t));
      setEditingTable(null);
      toast.success('Table updated successfully');
    } catch (error) {
      toast.error('Failed to update table');
    } finally {
      setSavingTable(false);
    }
  };

  const deleteTable = async (tableId) => {
    if (!confirm('Are you sure you want to delete this table?')) return;
    try {
      await axios.delete(`${API}/admin/tables/${tableId}`);
      setTables(tables.filter(t => t.id !== tableId));
      toast.success('Table deleted successfully');
    } catch (error) {
      toast.error('Failed to delete table');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      checkStripeStatus();
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const checkStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/status`);
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to check Stripe status:', error);
    }
  };

  const connectStripe = async () => {
    setConnectingStripe(true);
    try {
      // First create the account if needed
      await axios.post(`${API}/stripe/connect/create-account`);
      
      // Then get the onboarding link
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/stripe/connect/onboarding-link?origin_url=${encodeURIComponent(originUrl)}`);
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
      setConnectingStripe(false);
    }
  };

  const disconnectStripe = async () => {
    if (!window.confirm('Disconnect Stripe for this venue? New deposit payments cannot be taken until Stripe is reconnected. Existing bookings and payment history will remain.')) return;

    try {
      const response = await axios.post(`${API}/stripe/connect/disconnect`);
      setStripeStatus(response.data);
      const settingsResponse = await axios.get(`${API}/admin/settings`);
      setSettings(settingsResponse.data);
      updateRestaurant(settingsResponse.data);
      toast.success('Stripe disconnected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect Stripe');
    }
  };

  const connectGoogleCalendar = async () => {
    setConnectingGoogle(true);
    try {
      const res = await axios.get(`${API}/admin/google-calendar/authorize-url`);
      window.location.href = res.data.url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start Google Calendar connection');
      setConnectingGoogle(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar from this venue?')) return;
    try {
      await axios.post(`${API}/admin/google-calendar/disconnect`);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      updateRestaurant(response.data);
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const resyncGoogleCalendarLinked = async () => {
    if (!settings?.google_calendar?.connected) return;
    setGcalBulkBusy('resync');
    try {
      const payload = {};
      if (gcalRepairFrom.trim()) payload.from_date = gcalRepairFrom.trim();
      if (gcalRepairTo.trim()) payload.to_date = gcalRepairTo.trim();
      const res = await axios.post(`${API}/admin/google-calendar/resync-linked-events`, payload);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      updateRestaurant(response.data);
      const n = res.data.processed ?? 0;
      if (n === 0) {
        toast.info('No linked bookings matched that range', {
          description:
            'Resync only updates bookings that already store a Google event ID in DineSpot. If you used Remove linked, those IDs were cleared — use Push missing to Google instead. Also check that your From/To dates include your bookings’ dates (booking date in DineSpot, not “today”).',
          duration: 10000,
        });
      } else {
        toast.success(`Updated ${n} linked calendar event(s) from your bookings.`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not resync calendar events');
    } finally {
      setGcalBulkBusy(null);
    }
  };

  const pushMissingGoogleCalendarEvents = async () => {
    if (!settings?.google_calendar?.connected) return;
    setGcalBulkBusy('pushMissing');
    try {
      const payload = {};
      if (gcalRepairFrom.trim()) payload.from_date = gcalRepairFrom.trim();
      if (gcalRepairTo.trim()) payload.to_date = gcalRepairTo.trim();
      const res = await axios.post(`${API}/admin/google-calendar/create-missing-calendar-events`, payload);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      updateRestaurant(response.data);
      const n = res.data.processed ?? 0;
      if (n === 0) {
        toast.info('No bookings needed a new Google event', {
          description:
            'Either every booking in the range already has a calendar link, or none fall in the range. Leave dates empty to use from today up to 400 days ahead, or set From/To to match booking dates.',
          duration: 9000,
        });
      } else {
        toast.success(
          `Requested Google Calendar events for ${n} booking(s) that had no link. If some are missing, check Last sync error above.`,
        );
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create calendar events');
    } finally {
      setGcalBulkBusy(null);
    }
  };

  const openRemoveGoogleLinkedDialog = () => {
    setGcalRemoveConfirm('');
    setGcalRemoveOpen(true);
  };

  const submitRemoveGoogleLinkedEvents = async () => {
    const from = gcalRemoveFrom.trim();
    const to = gcalRemoveTo.trim();
    const source = gcalRemoveSource.trim();
    if (!from && !to && !source) {
      toast.error('Choose at least one filter: from date, to date, or source (e.g. kolandar_import).');
      return;
    }
    if (gcalRemoveConfirm.trim() !== 'REMOVE_LINKED_EVENTS') {
      toast.error('Type REMOVE_LINKED_EVENTS in the confirmation field.');
      return;
    }
    setGcalBulkBusy('remove');
    try {
      const body = { confirm: 'REMOVE_LINKED_EVENTS' };
      if (from) body.from_date = from;
      if (to) body.to_date = to;
      if (source) body.source = source;
      const res = await axios.post(`${API}/admin/google-calendar/remove-linked-events`, body);
      setGcalRemoveOpen(false);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      updateRestaurant(response.data);
      toast.success(`Removed ${res.data.removed ?? 0} calendar event(s). Bookings in DineSpot were not deleted.`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not remove calendar events');
    } finally {
      setGcalBulkBusy(null);
    }
  };

  const openPurgeDinespotLikeDialog = () => {
    setGcalPurgeConfirm('');
    setGcalPurgeOpen(true);
  };

  const submitPurgeDinespotLikeEvents = async () => {
    const from = gcalPurgeFrom.trim();
    const to = gcalPurgeTo.trim();
    if (!from || !to) {
      toast.error('Choose both from and to dates for the calendar scrub.');
      return;
    }
    if (gcalPurgeConfirm.trim() !== 'PURGE_DINESPOT_LIKE_CAL_EVENTS') {
      toast.error('Type PURGE_DINESPOT_LIKE_CAL_EVENTS in the confirmation field.');
      return;
    }
    setGcalBulkBusy('purge');
    try {
      const res = await axios.post(`${API}/admin/google-calendar/purge-dinespot-like-events`, {
        from_date: from,
        to_date: to,
        confirm: 'PURGE_DINESPOT_LIKE_CAL_EVENTS',
      });
      setGcalPurgeOpen(false);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
      updateRestaurant(response.data);
      toast.success(
        `Scanned ${res.data.scanned ?? 0} calendar event(s), deleted ${res.data.deleted ?? 0} that matched DineSpot.`,
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not scrub calendar');
    } finally {
      setGcalBulkBusy(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, settings);
      setSettings(response.data);
      updateRestaurant(response.data);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateSettings = (field, value) => {
    setSettings(prev => ({
      ...prev,
      settings: { ...prev.settings, [field]: value }
    }));
  };

  const updateBranding = (field, value) => {
    setSettings(prev => ({
      ...prev,
      branding: { ...prev.branding, [field]: value }
    }));
  };

  const updateHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        opening_hours: {
          ...prev.settings.opening_hours,
          [day]: { ...prev.settings.opening_hours[day], [field]: value }
        }
      }
    }));
  };

  // Deposit settings helpers
  const updateDepositSettings = (field, value) => {
    setSettings(prev => ({
      ...prev,
      deposit_settings: { ...prev.deposit_settings, [field]: value }
    }));
  };

  const addDepositRule = () => {
    const newRule = {
      ...DEFAULT_RULE,
      id: `rule-${Date.now()}`,
      name: `Rule ${(settings?.deposit_settings?.rules?.length || 0) + 1}`,
      priority: settings?.deposit_settings?.rules?.length || 0
    };
    setSettings(prev => ({
      ...prev,
      deposit_settings: {
        ...prev.deposit_settings,
        rules: [...(prev.deposit_settings?.rules || []), newRule]
      }
    }));
  };

  const updateRule = (ruleId, field, value) => {
    setSettings(prev => ({
      ...prev,
      deposit_settings: {
        ...prev.deposit_settings,
        rules: prev.deposit_settings.rules.map(r => 
          r.id === ruleId ? { ...r, [field]: value } : r
        )
      }
    }));
  };

  const deleteRule = (ruleId) => {
    setSettings(prev => ({
      ...prev,
      deposit_settings: {
        ...prev.deposit_settings,
        rules: prev.deposit_settings.rules.filter(r => r.id !== ruleId)
      }
    }));
  };

  const toggleRuleDay = (ruleId, day) => {
    setSettings(prev => ({
      ...prev,
      deposit_settings: {
        ...prev.deposit_settings,
        rules: prev.deposit_settings.rules.map(r => {
          if (r.id !== ruleId) return r;
          const days = r.days || [];
          return {
            ...r,
            days: days.includes(day) ? days.filter(d => d !== day) : [...days, day]
          };
        })
      }
    }));
  };

  const copyWidgetUrl = () => {
    const url = `${process.env.REACT_APP_PUBLIC_URL || window.location.origin}/book/${restaurant?.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Widget URL copied to clipboard');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Settings</h1>
            <p className="text-muted-foreground mt-1">Configure your restaurant and booking system</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </Button>
        </div>

        <Tabs value={settingsTab} onValueChange={setSettingsTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 h-auto p-1">
            <TabsTrigger value="profile" className="gap-2 py-2" data-testid="tab-profile">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="tables" className="gap-2 py-2" data-testid="tab-tables">
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Tables</span>
            </TabsTrigger>
            <TabsTrigger value="hours" className="gap-2 py-2" data-testid="tab-hours">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Hours</span>
            </TabsTrigger>
            <TabsTrigger value="booking" className="gap-2 py-2" data-testid="tab-booking">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Booking</span>
            </TabsTrigger>
            <TabsTrigger value="deposits" className="gap-2 py-2" data-testid="tab-deposits">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Deposits</span>
            </TabsTrigger>
            <TabsTrigger value="branding" className="gap-2 py-2" data-testid="tab-branding">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading">Restaurant Profile</CardTitle>
                <CardDescription>Basic information about your restaurant</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Restaurant Name</Label>
                    <Input
                      value={settings?.name || ''}
                      onChange={(e) => updateField('name', e.target.value)}
                      data-testid="settings-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={settings?.email || ''}
                      onChange={(e) => updateField('email', e.target.value)}
                      data-testid="settings-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={settings?.phone || ''}
                      onChange={(e) => updateField('phone', e.target.value)}
                      data-testid="settings-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={settings?.address || ''}
                      onChange={(e) => updateField('address', e.target.value)}
                      data-testid="settings-address"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={settings?.description || ''}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="A brief description of your restaurant..."
                    rows={3}
                    data-testid="settings-description"
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label>Booking Widget URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={`${process.env.REACT_APP_PUBLIC_URL || window.location.origin}/book/${restaurant?.id}`}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button variant="outline" size="icon" onClick={copyWidgetUrl} data-testid="copy-widget-url">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={() => window.open(`/book/${restaurant?.id}`, '_blank')}
                      data-testid="preview-widget"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Share this URL or embed it on your website for customers to make reservations.
                  </p>
                </div>

                {canManageUsers && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Staff PIN Access
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Add team members with 4-digit PIN access and choose what they can open after keypad login.
                        </p>
                      </div>

                      <div className="rounded-lg border p-4 space-y-4 bg-secondary/20">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                              value={newStaffUser.name}
                              onChange={(e) => setNewStaffUser((prev) => ({ ...prev, name: e.target.value }))}
                              placeholder="e.g. Front Desk"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>4-digit PIN</Label>
                            <Input
                              type="password"
                              inputMode="numeric"
                              maxLength={4}
                              value={newStaffUser.pin}
                              onChange={(e) => setNewStaffUser((prev) => ({ ...prev, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                              placeholder="1234"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Role</Label>
                          <Select
                            value={newStaffUser.role}
                            onValueChange={(v) => setNewStaffUser((prev) => ({
                              ...prev,
                              role: v,
                              permissions: v === 'admin' ? permissionOptions.map((p) => p.key) : prev.permissions,
                            }))}
                          >
                            <SelectTrigger className="w-full sm:w-56">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff (limited)</SelectItem>
                              <SelectItem value="admin">Admin (full access)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 bg-background">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">Phone bookings: no deposit by default</p>
                            <p className="text-xs text-muted-foreground">
                              After this user signs in with their PIN, the phone booking form starts with “No deposit required” checked.
                            </p>
                          </div>
                          <Switch
                            checked={newStaffUser.phone_bookings_skip_deposit}
                            onCheckedChange={(v) => setNewStaffUser((prev) => ({ ...prev, phone_bookings_skip_deposit: !!v }))}
                          />
                        </div>

                        {newStaffUser.role !== 'admin' && (
                          <div className="space-y-2">
                            <Label>Permissions</Label>
                            <div className="grid grid-cols-2 gap-2">
                              {permissionOptions.map((perm) => (
                                <label key={perm.key} className="flex items-center gap-2 text-sm rounded border px-3 py-2 bg-background">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4"
                                    checked={newStaffUser.permissions.includes(perm.key)}
                                    onChange={(e) => {
                                      setNewStaffUser((prev) => {
                                        const next = new Set(prev.permissions);
                                        if (e.target.checked) next.add(perm.key);
                                        else next.delete(perm.key);
                                        return { ...prev, permissions: [...next] };
                                      });
                                    }}
                                  />
                                  {perm.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        <Button type="button" onClick={createStaffUser} disabled={savingStaffUser}>
                          {savingStaffUser ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                          Add PIN User
                        </Button>
                      </div>

                      <div className="space-y-3">
                        <Label>Current PIN Users</Label>
                        {loadingStaffUsers ? (
                          <div className="py-4 text-sm text-muted-foreground">Loading users...</div>
                        ) : staffUsers.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No PIN users yet. Add one to enable keypad login for this venue.</p>
                        ) : (
                          <div className="space-y-2">
                            {staffUsers.map((staffUser) => (
                              <div key={staffUser.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-3 bg-background">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{staffUser.name}</p>
                                    <Badge variant={staffUser.role === 'admin' ? 'default' : 'secondary'}>
                                      {staffUser.role}
                                    </Badge>
                                    {!staffUser.is_active && <Badge variant="outline">Inactive</Badge>}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Access: {(staffUser.permissions || []).join(', ')}
                                  </p>
                                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                                    <span className="text-xs text-muted-foreground">Phone booking default: no deposit</span>
                                    <Switch
                                      checked={!!staffUser.phone_bookings_skip_deposit}
                                      onCheckedChange={(v) => patchStaffPhoneBookingsDefault(staffUser, v)}
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button type="button" size="sm" variant="outline" onClick={() => toggleStaffUserActive(staffUser)}>
                                    {staffUser.is_active ? 'Deactivate' : 'Activate'}
                                  </Button>
                                  <Button type="button" size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteStaffUser(staffUser)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tables Tab */}
          <TabsContent value="tables">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading">Table Management</CardTitle>
                <CardDescription>Configure your restaurant's tables for booking allocation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add New Table */}
                <div className="p-4 bg-secondary/30 rounded-lg space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Add New Table
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto]">
                    <div className="space-y-2">
                      <Label>Table Name/Number</Label>
                      <Input
                        placeholder="e.g., 1, A1, Patio 1"
                        value={newTable.name}
                        onChange={(e) => setNewTable({ ...newTable, name: e.target.value })}
                        data-testid="new-table-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Capacity (seats)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newTable.capacity}
                        onChange={(e) => setNewTable({ ...newTable, capacity: e.target.value })}
                        data-testid="new-table-capacity"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={createTable} 
                        disabled={savingTable || !newTable.name}
                        className="w-full sm:min-w-[140px]"
                        data-testid="add-table-btn"
                      >
                        {savingTable ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                        Add Table
                      </Button>
                    </div>
                  </div>
                  <TableCategoryPicker
                    value={newTable.category}
                    onChange={(category) => setNewTable({ ...newTable, category })}
                    knownCategories={knownTableCategories}
                    testIdPrefix="new"
                  />
                </div>

                <Separator />

                {/* Tables List */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Your Tables ({tables.length})</h3>
                    <p className="text-sm text-muted-foreground">
                      Total capacity: {tables.reduce((sum, t) => sum + (t.capacity || 0), 0)} seats
                    </p>
                  </div>

                  {loadingTables ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : tables.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <LayoutGrid className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No tables configured yet</p>
                      <p className="text-sm mt-1">Add your first table above to start allocating bookings</p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {tables.map((table) => (
                        <div 
                          key={table.id} 
                          className={`flex items-center gap-4 p-4 rounded-lg border ${
                            table.is_active !== false ? 'bg-background' : 'bg-secondary/50 opacity-60'
                          }`}
                          data-testid={`table-${table.id}`}
                        >
                          {editingTable?.id === table.id ? (
                            // Edit mode
                            <>
                              <div className="flex-1 space-y-3 min-w-0">
                                <div className="grid gap-3 sm:grid-cols-3">
                                  <Input
                                    value={editingTable.name}
                                    onChange={(e) => setEditingTable({ ...editingTable, name: e.target.value })}
                                    placeholder="Table name"
                                  />
                                  <Input
                                    type="number"
                                    min="1"
                                    value={editingTable.capacity}
                                    onChange={(e) => setEditingTable({ ...editingTable, capacity: e.target.value })}
                                    placeholder="Capacity"
                                  />
                                  <div className="flex items-center gap-2 sm:justify-end">
                                    <Switch
                                      checked={editingTable.is_active !== false}
                                      onCheckedChange={(checked) => setEditingTable({ ...editingTable, is_active: checked })}
                                    />
                                    <span className="text-sm">Active</span>
                                  </div>
                                </div>
                                <TableCategoryPicker
                                  value={editingTable.category}
                                  onChange={(category) => setEditingTable({ ...editingTable, category })}
                                  knownCategories={knownTableCategories}
                                  testIdPrefix="edit"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => updateTable(table.id)} disabled={savingTable}>
                                  {savingTable ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingTable(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </>
                          ) : (
                            // View mode
                            <>
                              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center font-semibold text-primary">
                                {table.name}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Table {table.name}</span>
                                  {table.category && (
                                    <Badge variant="secondary" className="text-xs">
                                      {table.category}
                                    </Badge>
                                  )}
                                  {table.is_active === false && (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                      Inactive
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {table.capacity} {table.capacity === 1 ? 'seat' : 'seats'}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  onClick={() => setEditingTable({ ...table })}
                                  data-testid={`edit-table-${table.id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => deleteTable(table.id)}
                                  data-testid={`delete-table-${table.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hours Tab */}
          <TabsContent value="hours">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading">Operating Hours</CardTitle>
                <CardDescription>Set your restaurant's opening times. Add multiple periods per day (e.g., Lunch & Dinner)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {DAYS.map((day) => {
                    const daySchedule = settings?.settings?.opening_hours_v2?.[day] || 
                      { closed: false, periods: [{ title: 'All Day', open: settings?.settings?.opening_hours?.[day]?.open || '11:00', close: settings?.settings?.opening_hours?.[day]?.close || '22:00' }] };
                    const isClosed = daySchedule.closed;
                    const periods = daySchedule.periods || [];
                    
                    return (
                      <div key={day} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium capitalize text-lg">{day}</span>
                          <div className="flex items-center gap-3">
                            {!isClosed && (
                              <Select
                                onValueChange={(targetDay) => {
                                  // Copy current day's schedule to target day
                                  const currentSchedule = { ...daySchedule };
                                  setSettings(prev => ({
                                    ...prev,
                                    settings: {
                                      ...prev.settings,
                                      opening_hours_v2: {
                                        ...prev.settings?.opening_hours_v2,
                                        [targetDay]: currentSchedule
                                      }
                                    }
                                  }));
                                  toast.success(`Copied ${day} hours to ${targetDay}`);
                                }}
                              >
                                <SelectTrigger className="w-[140px] h-8 text-xs">
                                  <Copy className="h-3 w-3 mr-1" />
                                  <SelectValue placeholder="Copy to..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {DAYS.filter(d => d !== day).map((targetDay) => (
                                    <SelectItem key={targetDay} value={targetDay} className="text-xs">
                                      {targetDay.charAt(0).toUpperCase() + targetDay.slice(1)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <Switch
                              checked={!isClosed}
                              onCheckedChange={(open) => {
                                const newSchedule = { ...daySchedule, closed: !open };
                                setSettings(prev => ({
                                  ...prev,
                                  settings: {
                                    ...prev.settings,
                                    opening_hours_v2: {
                                      ...prev.settings?.opening_hours_v2,
                                      [day]: newSchedule
                                    }
                                  }
                                }));
                              }}
                            />
                            <span className="text-sm text-muted-foreground">{isClosed ? 'Closed' : 'Open'}</span>
                          </div>
                        </div>
                        
                        {!isClosed && (
                          <>
                            <div className="mb-3 flex flex-col gap-2 rounded-md border border-border/60 bg-muted/15 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0 pr-2">
                                <Label className="text-sm font-medium">Table turn (this weekday)</Label>
                                <p className="mt-0.5 max-w-xl text-xs text-muted-foreground">
                                  How long a table stays reserved on the diary for overlaps (e.g. shorter Sunday,
                                  longer Saturday). Use default to follow the global “Table reservation length”
                                  in the Booking tab.
                                </p>
                              </div>
                              <Select
                                value={
                                  daySchedule.table_booking_duration_minutes != null &&
                                  daySchedule.table_booking_duration_minutes !== ''
                                    ? String(daySchedule.table_booking_duration_minutes)
                                    : 'inherit'
                                }
                                onValueChange={(v) => {
                                  setSettings((prev) => ({
                                    ...prev,
                                    settings: {
                                      ...prev.settings,
                                      opening_hours_v2: {
                                        ...prev.settings?.opening_hours_v2,
                                        [day]: {
                                          ...daySchedule,
                                          ...(v === 'inherit'
                                            ? { table_booking_duration_minutes: undefined }
                                            : { table_booking_duration_minutes: parseInt(v, 10) }),
                                        },
                                      },
                                    },
                                  }));
                                }}
                              >
                                <SelectTrigger
                                  className="h-9 w-full shrink-0 sm:w-[220px]"
                                  data-testid={`hours-table-turn-${day}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="inherit">
                                    Use booking default (
                                    {String(settings?.settings?.table_booking_duration_minutes ?? 120)} min)
                                  </SelectItem>
                                  <SelectItem value="60">1 hour</SelectItem>
                                  <SelectItem value="90">1.5 hours</SelectItem>
                                  <SelectItem value="120">2 hours</SelectItem>
                                  <SelectItem value="150">2.5 hours</SelectItem>
                                  <SelectItem value="180">3 hours</SelectItem>
                                  <SelectItem value="240">4 hours</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-3">
                            {periods.map((period, idx) => (
                              <div key={idx} className="flex items-center gap-3 bg-secondary/30 p-3 rounded-lg">
                                <Input
                                  value={period.title || ''}
                                  onChange={(e) => {
                                    const newPeriods = [...periods];
                                    newPeriods[idx] = { ...period, title: e.target.value };
                                    setSettings(prev => ({
                                      ...prev,
                                      settings: {
                                        ...prev.settings,
                                        opening_hours_v2: {
                                          ...prev.settings?.opening_hours_v2,
                                          [day]: { ...daySchedule, periods: newPeriods }
                                        }
                                      }
                                    }));
                                  }}
                                  placeholder="Period name (e.g., Lunch)"
                                  className="w-32"
                                />
                                <Input
                                  type="time"
                                  value={period.open || ''}
                                  onChange={(e) => {
                                    const newPeriods = [...periods];
                                    newPeriods[idx] = { ...period, open: e.target.value };
                                    setSettings(prev => ({
                                      ...prev,
                                      settings: {
                                        ...prev.settings,
                                        opening_hours_v2: {
                                          ...prev.settings?.opening_hours_v2,
                                          [day]: { ...daySchedule, periods: newPeriods }
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-28"
                                />
                                <span className="text-muted-foreground">to</span>
                                <Input
                                  type="time"
                                  value={period.close || ''}
                                  onChange={(e) => {
                                    const newPeriods = [...periods];
                                    newPeriods[idx] = { ...period, close: e.target.value };
                                    setSettings(prev => ({
                                      ...prev,
                                      settings: {
                                        ...prev.settings,
                                        opening_hours_v2: {
                                          ...prev.settings?.opening_hours_v2,
                                          [day]: { ...daySchedule, periods: newPeriods }
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-28"
                                />
                                {periods.length > 1 && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-destructive"
                                    onClick={() => {
                                      const newPeriods = periods.filter((_, i) => i !== idx);
                                      setSettings(prev => ({
                                        ...prev,
                                        settings: {
                                          ...prev.settings,
                                          opening_hours_v2: {
                                            ...prev.settings?.opening_hours_v2,
                                            [day]: { ...daySchedule, periods: newPeriods }
                                          }
                                        }
                                      }));
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newPeriods = [...periods, { title: '', open: '18:00', close: '22:00' }];
                                setSettings(prev => ({
                                  ...prev,
                                  settings: {
                                    ...prev.settings,
                                    opening_hours_v2: {
                                      ...prev.settings?.opening_hours_v2,
                                      [day]: { ...daySchedule, periods: newPeriods }
                                    }
                                  }
                                }));
                              }}
                              className="w-full"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add Time Period
                            </Button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Booking Tab */}
          <TabsContent value="booking">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading">Booking Configuration</CardTitle>
                <CardDescription>Control how customers can book tables</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Total Tables</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings?.settings?.total_tables || 10}
                      onChange={(e) => updateSettings('total_tables', parseInt(e.target.value))}
                      data-testid="settings-total-tables"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tables Per Time Slot</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings?.settings?.tables_per_slot || 4}
                      onChange={(e) => updateSettings('tables_per_slot', parseInt(e.target.value))}
                      data-testid="settings-tables-per-slot"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Booking Interval</Label>
                    <Select
                      value={String(settings?.settings?.booking_interval_minutes || 30)}
                      onValueChange={(v) => updateSettings('booking_interval_minutes', parseInt(v))}
                    >
                      <SelectTrigger data-testid="settings-interval">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Table reservation length</Label>
                    <Select
                      value={String(settings?.settings?.table_booking_duration_minutes ?? 120)}
                      onValueChange={(v) => updateSettings('table_booking_duration_minutes', parseInt(v, 10))}
                    >
                      <SelectTrigger data-testid="settings-table-booking-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="90">1.5 hours</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                        <SelectItem value="150">2.5 hours</SelectItem>
                        <SelectItem value="180">3 hours</SelectItem>
                        <SelectItem value="240">4 hours</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default overlap window when a weekday does not set its own under Operating hours. Staff
                      assignments cannot overlap another active booking on the same table until completed,
                      cancelled, or no-show.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Maximum Party Size</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings?.settings?.max_party_size || 12}
                      onChange={(e) => updateSettings('max_party_size', parseInt(e.target.value))}
                      data-testid="settings-max-party"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Advance Booking (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="5"
                      value={
                        settings?.settings?.min_advance_minutes != null
                          ? settings.settings.min_advance_minutes
                          : settings?.settings?.min_advance_hours != null
                            ? Math.round(Number(settings.settings.min_advance_hours) * 60)
                            : 120
                      }
                      onChange={(e) => {
                        const minutes = Math.max(0, parseInt(e.target.value, 10) || 0);
                        updateSettings('min_advance_minutes', minutes);
                      }}
                      data-testid="settings-min-advance"
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimum minutes in advance a customer must book (e.g. 30 = half an hour, 120 = two hours).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Max Advance Booking (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings?.settings?.max_advance_days || 30}
                      onChange={(e) => updateSettings('max_advance_days', parseInt(e.target.value))}
                      data-testid="settings-max-advance"
                    />
                  </div>
                </div>

                <div className="space-y-4 rounded-lg border border-border/60 bg-muted/10 p-4" data-testid="google-calendar-settings">
                  <h3 className="font-medium flex flex-wrap items-center gap-3">
                    <img
                      src={brandUrl('google-calendar.svg')}
                      alt=""
                      width={36}
                      height={36}
                      className="h-9 w-9 shrink-0 rounded-md object-contain"
                      loading="lazy"
                    />
                    <span>Google Calendar</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Connect one Google account for this venue. New bookings appear on that calendar; cancellations
                    remove events. Staff use the same Google account — no per-user Google sign-in here.
                  </p>
                  {settings?.google_calendar?.last_sync_error && (
                    <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                      Last sync error: {settings.google_calendar.last_sync_error}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    {settings?.google_calendar?.connected ? (
                      <Button type="button" variant="outline" onClick={disconnectGoogleCalendar}>
                        Disconnect Google Calendar
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={connectGoogleCalendar}
                        disabled={connectingGoogle}
                        className="gap-2"
                      >
                        {connectingGoogle ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                        Connect Google Calendar
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {settings?.google_calendar?.connected
                      ? 'Google Calendar is connected for this venue. Staff can disconnect it here at any time.'
                      : 'Connect Google Calendar to automatically sync confirmed bookings and cancellations.'}
                  </p>
                  {settings?.google_calendar?.connected && (
                    <div className="space-y-3 rounded-md border border-border/50 bg-background/80 p-3">
                      <p className="text-sm text-muted-foreground">
                        If import or edits put events on the wrong days, fix the booking dates in DineSpot first, then
                        use <span className="font-medium text-foreground">Resync linked events</span> to push the
                        current date and time to Google (only for bookings that still have a calendar link). Use{' '}
                        <span className="font-medium text-foreground">Push missing to Google</span> after Remove
                        linked or Scrub, to create new calendar events for bookings in the date range that have no
                        link yet. To delete wrong Google events only (and keep bookings), use{' '}
                        <span className="font-medium text-foreground">Remove linked events</span> with a filter such as
                        source <code className="text-xs">kolandar_import</code>. If Koalendar is still writing to this
                        same Google calendar, you can see an extra block per booking (often with your venue name and
                        address); turn off Koalendar&apos;s Google sync if you only want DineSpot. If DineSpot-style
                        blocks remain after Remove linked, use{' '}
                        <span className="font-medium text-foreground">Scrub DineSpot blocks in Google</span> for a date
                        range — it removes calendar events whose description still contains a DineSpot manage/cancel
                        link.
                      </p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="gcal-repair-from" className="text-xs">
                            From (optional)
                          </Label>
                          <Input
                            id="gcal-repair-from"
                            type="date"
                            className="w-[11rem]"
                            value={gcalRepairFrom}
                            onChange={(e) => setGcalRepairFrom(e.target.value)}
                            disabled={!!gcalBulkBusy}
                            data-testid="gcal-repair-from"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="gcal-repair-to" className="text-xs">
                            To (optional)
                          </Label>
                          <Input
                            id="gcal-repair-to"
                            type="date"
                            className="w-[11rem]"
                            value={gcalRepairTo}
                            onChange={(e) => setGcalRepairTo(e.target.value)}
                            disabled={!!gcalBulkBusy}
                            data-testid="gcal-repair-to"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          className="gap-2"
                          onClick={resyncGoogleCalendarLinked}
                          disabled={!!gcalBulkBusy}
                          data-testid="gcal-resync-linked"
                        >
                          {gcalBulkBusy === 'resync' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          Resync linked events
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="gap-2"
                          onClick={pushMissingGoogleCalendarEvents}
                          disabled={!!gcalBulkBusy}
                          data-testid="gcal-push-missing"
                        >
                          {gcalBulkBusy === 'pushMissing' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Calendar className="h-4 w-4" />
                          )}
                          Push missing to Google
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          className="gap-2"
                          onClick={openRemoveGoogleLinkedDialog}
                          disabled={!!gcalBulkBusy}
                          data-testid="gcal-remove-linked-open"
                        >
                          Remove linked events…
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          onClick={openPurgeDinespotLikeDialog}
                          disabled={!!gcalBulkBusy}
                          data-testid="gcal-purge-like-open"
                        >
                          Scrub DineSpot blocks in Google…
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Dialog open={gcalRemoveOpen} onOpenChange={setGcalRemoveOpen}>
                  <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <DialogHeader>
                      <DialogTitle>Remove linked Google Calendar events</DialogTitle>
                      <DialogDescription>
                        This deletes matching events from Google Calendar and clears the link on each booking. Your
                        bookings in DineSpot are not deleted. You must choose at least one filter so the whole calendar
                        is not cleared by mistake.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-3 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="gcal-remove-from">From (optional)</Label>
                          <Input
                            id="gcal-remove-from"
                            type="date"
                            value={gcalRemoveFrom}
                            onChange={(e) => setGcalRemoveFrom(e.target.value)}
                            disabled={gcalBulkBusy === 'remove'}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="gcal-remove-to">To (optional)</Label>
                          <Input
                            id="gcal-remove-to"
                            type="date"
                            value={gcalRemoveTo}
                            onChange={(e) => setGcalRemoveTo(e.target.value)}
                            disabled={gcalBulkBusy === 'remove'}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="gcal-remove-source">Source (optional)</Label>
                        <Input
                          id="gcal-remove-source"
                          placeholder="e.g. kolandar_import"
                          value={gcalRemoveSource}
                          onChange={(e) => setGcalRemoveSource(e.target.value)}
                          disabled={gcalBulkBusy === 'remove'}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="gcal-remove-confirm">Type REMOVE_LINKED_EVENTS to confirm</Label>
                        <Input
                          id="gcal-remove-confirm"
                          autoComplete="off"
                          value={gcalRemoveConfirm}
                          onChange={(e) => setGcalRemoveConfirm(e.target.value)}
                          disabled={gcalBulkBusy === 'remove'}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setGcalRemoveOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="gap-2"
                        onClick={submitRemoveGoogleLinkedEvents}
                        disabled={gcalBulkBusy === 'remove'}
                      >
                        {gcalBulkBusy === 'remove' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Remove events
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={gcalPurgeOpen} onOpenChange={setGcalPurgeOpen}>
                  <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <DialogHeader>
                      <DialogTitle>Scrub DineSpot blocks in Google Calendar</DialogTitle>
                      <DialogDescription>
                        This scans events whose start time falls in the range you pick and deletes those whose
                        description contains a DineSpot manage/cancel link (the same line DineSpot puts in event
                        details). Use it when Remove linked reported removals but wrong blocks are still visible, or to
                        clear duplicates. It does not change bookings in DineSpot. Range is limited to 400 days.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-3 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="gcal-purge-from">From</Label>
                          <Input
                            id="gcal-purge-from"
                            type="date"
                            value={gcalPurgeFrom}
                            onChange={(e) => setGcalPurgeFrom(e.target.value)}
                            disabled={gcalBulkBusy === 'purge'}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="gcal-purge-to">To</Label>
                          <Input
                            id="gcal-purge-to"
                            type="date"
                            value={gcalPurgeTo}
                            onChange={(e) => setGcalPurgeTo(e.target.value)}
                            disabled={gcalBulkBusy === 'purge'}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="gcal-purge-confirm">Type PURGE_DINESPOT_LIKE_CAL_EVENTS to confirm</Label>
                        <Input
                          id="gcal-purge-confirm"
                          autoComplete="off"
                          value={gcalPurgeConfirm}
                          onChange={(e) => setGcalPurgeConfirm(e.target.value)}
                          disabled={gcalBulkBusy === 'purge'}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setGcalPurgeOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="gap-2"
                        onClick={submitPurgeDinespotLikeEvents}
                        disabled={gcalBulkBusy === 'purge'}
                      >
                        {gcalBulkBusy === 'purge' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Scrub calendar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Separator />

                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Notifications
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Booking Confirmations</Label>
                      <p className="text-sm text-muted-foreground">Send email when booking is made</p>
                    </div>
                    <Switch
                      checked={settings?.settings?.email_confirmations_enabled ?? true}
                      onCheckedChange={(v) => updateSettings('email_confirmations_enabled', v)}
                      data-testid="settings-email-confirmations"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Booking Reminders</Label>
                      <p className="text-sm text-muted-foreground">Send reminder before reservation</p>
                    </div>
                    <Switch
                      checked={settings?.settings?.email_reminders_enabled ?? true}
                      onCheckedChange={(v) => updateSettings('email_reminders_enabled', v)}
                      data-testid="settings-email-reminders"
                    />
                  </div>
                  {settings?.settings?.email_reminders_enabled && (
                    <div className="ml-6 p-4 bg-secondary/30 rounded-lg space-y-3">
                      <div className="space-y-2">
                        <Label>Reminder Timing</Label>
                        <Select
                          value={String(settings?.settings?.reminder_hours_before || 24)}
                          onValueChange={(v) => updateSettings('reminder_hours_before', parseInt(v))}
                        >
                          <SelectTrigger data-testid="settings-reminder-hours" className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2 hours before</SelectItem>
                            <SelectItem value="6">6 hours before</SelectItem>
                            <SelectItem value="12">12 hours before</SelectItem>
                            <SelectItem value="24">24 hours before</SelectItem>
                            <SelectItem value="48">48 hours before</SelectItem>
                            <SelectItem value="72">72 hours before</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">How long before the booking to send the reminder email</p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Booking Widget Messages
                  </h3>
                  <p className="text-sm text-muted-foreground">Display custom messages to customers during booking</p>
                  <div className="space-y-2">
                    <Label>Top Message</Label>
                    <Textarea
                      value={settings?.settings?.booking_message_top || ''}
                      onChange={(e) => updateSettings('booking_message_top', e.target.value || null)}
                      placeholder="e.g., Please note: Sundays are busy - 2 hour table limit applies"
                      rows={2}
                      data-testid="settings-booking-message-top"
                    />
                    <p className="text-xs text-muted-foreground">Shown at the top of time slot selection</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Bottom Message</Label>
                    <Textarea
                      value={settings?.settings?.booking_message_bottom || ''}
                      onChange={(e) => updateSettings('booking_message_bottom', e.target.value || null)}
                      placeholder="e.g., Large parties may be seated in our private dining area"
                      rows={2}
                      data-testid="settings-booking-message-bottom"
                    />
                    <p className="text-xs text-muted-foreground">Shown below the time slots</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deposits Tab */}
          <TabsContent value="deposits">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Deposit Rules
                </CardTitle>
                <CardDescription>
                  Configure when deposits are required for bookings. Rules are evaluated in order - first matching rule wins.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Deposits */}
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                  <div>
                    <Label className="text-base">Enable Deposits</Label>
                    <p className="text-sm text-muted-foreground">
                      Require deposits for certain bookings via Stripe
                    </p>
                  </div>
                  <Switch
                    checked={settings?.deposit_settings?.enabled ?? false}
                    onCheckedChange={(v) => updateDepositSettings('enabled', v)}
                    data-testid="deposits-enabled"
                  />
                </div>

                {settings?.deposit_settings?.enabled && (
                  <>
                    {/* Default Currency */}
                    <div className="space-y-2">
                      <Label>Default Currency</Label>
                      <Select
                        value={settings?.deposit_settings?.default_currency || 'gbp'}
                        onValueChange={(v) => updateDepositSettings('default_currency', v)}
                      >
                        <SelectTrigger className="w-40" data-testid="deposit-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gbp">GBP (£)</SelectItem>
                          <SelectItem value="usd">USD ($)</SelectItem>
                          <SelectItem value="eur">EUR (€)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    {/* Rules List */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">Deposit Rules</h3>
                        <Button onClick={addDepositRule} variant="outline" size="sm" className="gap-2" data-testid="add-rule-btn">
                          <Plus className="h-4 w-4" />
                          Add Rule
                        </Button>
                      </div>

                      {(!settings?.deposit_settings?.rules || settings.deposit_settings.rules.length === 0) ? (
                        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                          <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p>No deposit rules configured</p>
                          <p className="text-sm">Add a rule to start requiring deposits</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {settings.deposit_settings.rules.map((rule, index) => (
                            <Card key={rule.id} className="border-border/50">
                              <CardContent className="p-4 space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex items-center gap-2">
                                    <GripVertical className="h-5 w-5 text-muted-foreground" />
                                    <Badge variant="outline">#{index + 1}</Badge>
                                    <Input
                                      value={rule.name}
                                      onChange={(e) => updateRule(rule.id, 'name', e.target.value)}
                                      placeholder="Rule name"
                                      className="w-48"
                                      data-testid={`rule-${index}-name`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={rule.enabled}
                                      onCheckedChange={(v) => updateRule(rule.id, 'enabled', v)}
                                      data-testid={`rule-${index}-enabled`}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => deleteRule(rule.id)}
                                      className="text-destructive hover:text-destructive"
                                      data-testid={`rule-${index}-delete`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>

                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                  {/* Days Selection */}
                                  <div className="sm:col-span-2 space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                      Days of Week
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-[250px]">
                                            <p>Select which days this rule applies to. Leave all unselected to apply to every day.</p>
                                            <p className="mt-1 text-xs text-muted-foreground">Example: Select Fri, Sat, Sun for weekend deposits only.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </Label>
                                    <div className="flex flex-wrap gap-1">
                                      {DAYS.map((day) => (
                                        <button
                                          key={day}
                                          type="button"
                                          onClick={() => toggleRuleDay(rule.id, day)}
                                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                            rule.days?.includes(day)
                                              ? 'bg-primary text-primary-foreground'
                                              : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                                          }`}
                                          data-testid={`rule-${index}-day-${day}`}
                                        >
                                          {DAY_LABELS[day]}
                                        </button>
                                      ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {rule.days?.length > 0 ? `Applies to: ${rule.days.map(d => DAY_LABELS[d]).join(', ')}` : 'Applies to all days'}
                                    </p>
                                  </div>

                                  {/* Specific Dates Selection */}
                                  <div className="sm:col-span-2 space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                      Specific Dates
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-[250px]">
                                            <p>Add specific dates when this rule should apply (e.g., Valentine's Day, Christmas Eve).</p>
                                            <p className="mt-1 text-xs text-muted-foreground">These dates take priority over the day-of-week selection.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </Label>
                                    <div className="flex gap-2">
                                      <Input
                                        type="date"
                                        className="flex-1"
                                        onChange={(e) => {
                                          const newDate = e.target.value;
                                          if (newDate && !(rule.dates || []).includes(newDate)) {
                                            updateRule(rule.id, 'dates', [...(rule.dates || []), newDate]);
                                          }
                                          e.target.value = '';
                                        }}
                                        data-testid={`rule-${index}-add-date`}
                                      />
                                    </div>
                                    {(rule.dates || []).length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {(rule.dates || []).map((date) => (
                                          <Badge 
                                            key={date} 
                                            variant="secondary"
                                            className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                                            onClick={() => {
                                              updateRule(rule.id, 'dates', (rule.dates || []).filter(d => d !== date));
                                            }}
                                          >
                                            <Calendar className="h-3 w-3" />
                                            {new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            <span className="ml-1">×</span>
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                      Click a date badge to remove it. Specific dates override day-of-week settings.
                                    </p>
                                  </div>

                                  {/* Min Party Size */}
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                      Min Party Size
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-[250px]">
                                            <p>This rule only applies to bookings with this many guests or more. Set to 1 to apply to all bookings.</p>
                                            <p className="mt-1 text-xs text-muted-foreground">Example: Set to 6 to only require deposits for large groups.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={rule.min_party_size}
                                      onChange={(e) => updateRule(rule.id, 'min_party_size', parseInt(e.target.value))}
                                      data-testid={`rule-${index}-min-party`}
                                    />
                                  </div>

                                  {/* Deposit Type */}
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                      Deposit Type
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-[250px]">
                                            <p><strong>Fixed Amount:</strong> Same deposit regardless of party size (e.g., £20 total)</p>
                                            <p className="mt-1"><strong>Per Person:</strong> Multiply amount by number of guests (e.g., £5 × 4 guests = £20)</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </Label>
                                    <Select
                                      value={rule.deposit_type}
                                      onValueChange={(v) => updateRule(rule.id, 'deposit_type', v)}
                                    >
                                      <SelectTrigger data-testid={`rule-${index}-type`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                                        <SelectItem value="per_person">Per Person</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  {/* Amount */}
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                      Amount ({rule.deposit_type === 'per_person' ? 'per person' : 'total'})
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-[250px]">
                                            {rule.deposit_type === 'per_person' ? (
                                              <p>Amount charged per guest. A party of 4 would pay 4× this amount.</p>
                                            ) : (
                                              <p>Fixed amount charged regardless of party size.</p>
                                            )}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={rule.amount}
                                      onChange={(e) => updateRule(rule.id, 'amount', parseFloat(e.target.value))}
                                      data-testid={`rule-${index}-amount`}
                                    />
                                  </div>

                                  {/* Refundable */}
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                      Refundable?
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-[250px]">
                                            <p>If enabled, customers can get their deposit back if they cancel before the deadline.</p>
                                            <p className="mt-1 text-xs text-muted-foreground">Non-refundable deposits are kept regardless of cancellation.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </Label>
                                    <div className="flex items-center gap-2 h-10">
                                      <Switch
                                        checked={rule.is_refundable}
                                        onCheckedChange={(v) => updateRule(rule.id, 'is_refundable', v)}
                                        data-testid={`rule-${index}-refundable`}
                                      />
                                      <span className="text-sm text-muted-foreground">
                                        {rule.is_refundable ? 'Yes' : 'No'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Refund Hours */}
                                  {rule.is_refundable && (
                                    <div className="space-y-2">
                                      <Label className="flex items-center gap-1.5">
                                        Refund Deadline
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-[250px]">
                                              <p>Hours before the booking when refunds are no longer available.</p>
                                              <p className="mt-1 text-xs text-muted-foreground">Example: 24 hours means customers must cancel at least 1 day before to get a refund.</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </Label>
                                      <div className="flex items-center gap-2">
                                        <Input
                                          type="number"
                                          min="1"
                                          value={rule.refund_hours_before}
                                          onChange={(e) => updateRule(rule.id, 'refund_hours_before', parseInt(e.target.value))}
                                          data-testid={`rule-${index}-refund-hours`}
                                          className="flex-1"
                                        />
                                        <span className="text-sm text-muted-foreground">hours before</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Terms & Conditions Section */}
                    {settings?.deposit_settings?.enabled && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Deposit Terms & Conditions
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const defaultTerms = `BOOKING DEPOSIT TERMS & CONDITIONS

By paying this deposit, you agree to the following terms:

1. DEPOSIT PURPOSE
Restaurants prepare staffing, tables and ingredients around confirmed bookings. This deposit secures your reservation, helps reduce last-minute no-shows, and will be deducted from your final bill.

2. CANCELLATION POLICY
• Cancellations made more than {refund_hours} hours before your booking: {refund_fee_policy}
• Cancellations made less than {refund_hours} hours before your booking: Deposit is non-refundable
• No-shows: Deposit is non-refundable

3. CHANGES AND MOVED BOOKINGS
If you need to change your booking (date, time or party size), please contact us as soon as possible. Changes are subject to availability and must be agreed with us.
If we move your booking to a new date or time at your request, your deposit is carried over to the new booking. Whether it remains refundable depends on when the move is made:
• Moves made more than {refund_hours} hours before your ORIGINAL booking time: your deposit stays refundable. The cancellation policy above then applies to your NEW booking time.
• Moves made less than {refund_hours} hours before your ORIGINAL booking time (when your deposit would already be non-refundable): your deposit is carried over as NON-REFUNDABLE. It will not become refundable again, even if your new booking is more than {refund_hours} hours away, and it will not be refunded if you later cancel, move again or do not attend.
A deposit carried over to a moved booking is still deducted from your final bill. We will confirm any move, and what it means for your deposit, by email.

4. REFUNDS
Refunds will be processed within 5-10 business days to your original payment method.

5. AGREEMENT
These terms constitute an agreement between you and the restaurant. By proceeding with payment, you confirm that you have read, understood, and agree to these terms.

For any questions, please contact the restaurant directly.`;
                              updateDepositSettings('terms_and_conditions', defaultTerms);
                            }}
                          >
                            Reset to Default
                          </Button>
                        </div>
                        
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-800">
                              <p className="font-medium">Important Legal Notice</p>
                              <p className="mt-1">These terms form a contract between your restaurant and the customer. Ensure they accurately reflect your policies. Customers must accept these terms before paying their deposit.</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-secondary/30 p-4">
                          <div>
                            <Label className="text-base">Deduct card transaction fees from refunds</Label>
                            <p className="mt-1 text-sm text-muted-foreground">
                              If enabled, eligible deposit refunds are returned minus the original Stripe card
                              processing fee. Customers see this before accepting terms and paying.
                            </p>
                          </div>
                          <Switch
                            checked={settings?.deposit_settings?.deduct_transaction_fees_from_refunds ?? false}
                            onCheckedChange={(v) => updateDepositSettings('deduct_transaction_fees_from_refunds', v)}
                            data-testid="deduct-transaction-fees-from-refunds"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="flex items-center gap-1.5">
                            Terms & Conditions Text
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[300px]">
                                  <p>Customize the terms shown to customers before they pay their deposit. Include your cancellation policy, refund terms, and any other conditions.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <Textarea
                            value={settings?.deposit_settings?.terms_and_conditions || ''}
                            onChange={(e) => updateDepositSettings('terms_and_conditions', e.target.value)}
                            placeholder="Enter your deposit terms and conditions..."
                            className="min-h-[300px] font-mono text-sm"
                            data-testid="deposit-terms"
                          />
                          <p className="text-xs text-muted-foreground">
                            This will be displayed to customers when making a booking that requires a deposit. They must accept these terms to proceed.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <Separator />

                {/* Stripe Connect Section */}
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-medium flex flex-wrap items-center gap-3">
                      <img
                        src={brandUrl('stripe-wordmark.svg')}
                        alt=""
                        width={120}
                        height={32}
                        className="h-7 w-auto max-w-[140px] shrink-0 object-contain object-left"
                        loading="lazy"
                      />
                      <span className="text-foreground">Stripe Account</span>
                    </h3>
                    <Badge
                      variant={stripeStatus?.mode === 'live' ? 'default' : 'secondary'}
                      className={cn(
                        stripeStatus?.mode === 'live'
                          ? 'bg-emerald-600 text-white'
                          : stripeStatus?.mode === 'test'
                            ? 'bg-amber-100 text-amber-800'
                            : ''
                      )}
                    >
                      Stripe {stripeStatus?.mode === 'live' ? 'Live' : stripeStatus?.mode === 'test' ? 'Test' : 'Not configured'}
                    </Badge>
                  </div>

                  {stripeChargesLive ? (
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="h-5 w-5 text-emerald-600" />
                              <div>
                                <p className="font-medium text-emerald-800">Stripe Connected</p>
                                <p className="text-sm text-emerald-700">
                                  Your Stripe account is active and ready to receive deposits.
                                </p>
                              </div>
                            </div>
                            <Button type="button" variant="outline" onClick={disconnectStripe}>
                              Disconnect Stripe
                            </Button>
                          </div>
                        </div>
                      ) : stripeAccountPresent ? (
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-amber-800">
                                  {stripeOnboardingComplete ? 'Stripe linked — charges not enabled yet' : 'Onboarding incomplete'}
                                </p>
                                <p className="text-sm text-amber-700 mt-1">
                                  {stripeOnboardingComplete
                                    ? 'Finish any outstanding Stripe requirements, or disconnect to remove this account and connect a different one.'
                                    : 'Complete Stripe onboarding to take deposits. You can disconnect anytime to link a different Stripe account.'}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                              <Button
                                type="button"
                                onClick={connectStripe}
                                disabled={connectingStripe}
                                variant="outline"
                                className="border-amber-300"
                                data-testid="complete-stripe-btn"
                              >
                                {connectingStripe ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue Stripe setup'}
                              </Button>
                              <Button type="button" variant="ghost" onClick={disconnectStripe}>
                                Disconnect Stripe
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="font-medium text-blue-900">Connect Your Stripe Account</p>
                              <p className="text-sm text-blue-700 mt-1">
                                Connect your Stripe account to start receiving deposits directly.
                              </p>
                              <p className="text-xs text-muted-foreground mt-2 max-w-md">
                                Need the instructions by email? Your DineSpot platform admin can send a setup guide to
                                your company email from the platform dashboard.
                              </p>
                            </div>
                            <Button
                              onClick={connectStripe}
                              disabled={connectingStripe}
                              data-testid="connect-stripe-btn"
                            >
                              {connectingStripe ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <LinkIcon className="h-4 w-4 mr-2" />
                              )}
                              Connect Stripe
                            </Button>
                          </div>
                        </div>
                      )}
                      
                  <p className="text-xs text-muted-foreground">
                    Deposits are paid directly to your Stripe account. DineSpot does not hold your funds.
                    Test mode uses Stripe test keys and test connected accounts; switching to live Stripe requires live
                    API keys and each venue reconnecting its Stripe account in live mode.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading">White-Label Branding</CardTitle>
                <CardDescription>Customize the appearance of your booking widget</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings?.branding?.primary_color || '#1a1f2c'}
                        onChange={(e) => updateBranding('primary_color', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        data-testid="settings-primary-color"
                      />
                      <Input
                        value={settings?.branding?.primary_color || '#1a1f2c'}
                        onChange={(e) => updateBranding('primary_color', e.target.value)}
                        className="flex-1 font-mono"
                        placeholder="#1a1f2c"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">Main brand color for buttons and accents</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings?.branding?.accent_color || '#10b981'}
                        onChange={(e) => updateBranding('accent_color', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        data-testid="settings-accent-color"
                      />
                      <Input
                        value={settings?.branding?.accent_color || '#10b981'}
                        onChange={(e) => updateBranding('accent_color', e.target.value)}
                        className="flex-1 font-mono"
                        placeholder="#10b981"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">Secondary color for highlights</p>
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Logo URL</Label>
                    <Input
                      value={settings?.branding?.logo_url || ''}
                      onChange={(e) => updateBranding('logo_url', e.target.value)}
                      placeholder="https://your-restaurant.com/logo.png"
                      data-testid="settings-logo-url"
                    />
                    <p className="text-sm text-muted-foreground">URL to your restaurant logo (displayed in widget header)</p>
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Hero Image URL</Label>
                    <Input
                      value={settings?.branding?.hero_image_url || ''}
                      onChange={(e) => updateBranding('hero_image_url', e.target.value)}
                      placeholder="https://your-restaurant.com/hero.jpg"
                      data-testid="settings-hero-image"
                    />
                    <p className="text-sm text-muted-foreground">Large banner image shown at top of booking widget (recommended: 1200x600px)</p>
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Tagline</Label>
                    <Input
                      value={settings?.branding?.tagline || ''}
                      onChange={(e) => updateBranding('tagline', e.target.value)}
                      placeholder="e.g., Fine Dining Experience"
                      data-testid="settings-tagline"
                    />
                    <p className="text-sm text-muted-foreground">Short description shown below restaurant name</p>
                  </div>
                </div>

                {/* Preview */}
                <Separator />
                <div>
                  <Label className="mb-3 block">Widget Preview</Label>
                  <div 
                    className="border rounded-xl overflow-hidden max-w-sm"
                    style={{ 
                      '--preview-primary': settings?.branding?.primary_color || '#1a1f2c',
                      '--preview-accent': settings?.branding?.accent_color || '#10b981'
                    }}
                  >
                    {/* Hero Image Preview */}
                    {settings?.branding?.hero_image_url && (
                      <div className="relative h-40 overflow-hidden">
                        <img 
                          src={settings.branding.hero_image_url} 
                          alt="Hero" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        {settings?.branding?.logo_url ? (
                          <img 
                            src={settings.branding.logo_url} 
                            alt="Logo" 
                            className="h-10 w-10 object-contain rounded"
                          />
                        ) : (
                          <div 
                            className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: 'var(--preview-primary)' }}
                          >
                            {settings?.name?.[0] || 'R'}
                          </div>
                        )}
                        <div>
                          <h3 className="font-heading font-semibold">{settings?.name || 'Restaurant'}</h3>
                          <p className="text-xs text-muted-foreground">
                            {settings?.branding?.tagline || 'Book a Table'}
                          </p>
                        </div>
                      </div>
                      <Button 
                        className="w-full"
                        style={{ backgroundColor: 'var(--preview-primary)' }}
                      >
                        Select Date & Time
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
